import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'
import clsx from 'clsx'

import { TApi, TRoutes, TSupporterRoles } from '../../shared/const'
import http from '../../shared/http'
import i18nInstance from '../../shared/i18n'
import { emptyJuniorAlumni, juniorAlumniErrors, juniorAlumniPayload } from '../../shared/juniorAlumni'

import Btn from '../../UI/Btn'
import GdprConsentNote from '../../UI/GdprConsentNote'
import JuniorAlumniField from '../../UI/JuniorAlumniField'
import { PlainInput } from '../../UI/PlainField'
import StatesField from '../../UI/StatesField'

import { getRegisterContent } from './content'
import { HERO_PHOTO, PHOTOS } from './photos'
import { useReveal, Photo } from './Reveal'
import styles from './style.module.scss'

import logoIcon from '../../assets/icons/logo.svg'

/**
 * Abschnitt, der beim Hereinscrollen einblendet. Ohne
 * IntersectionObserver (Tests) ist er von Anfang an sichtbar.
 */
function Section({ as: Tag = 'section', className, children, ...rest }) {
	const [ref, shown] = useReveal()
	return (
		<Tag ref={ref} className={clsx(styles.reveal, shown && styles.revealIn, className)} {...rest}>
			{children}
		</Tag>
	)
}

/**
 * Ein nummerierter Block des Formulars.
 *
 * fieldset/legend statt div/h3: Die Gruppierung ist damit nicht nur sichtbar,
 * sondern wird auch von Screenreadern angesagt ("Zu Ihrer Person, Gruppe").
 * legend liegt bewusst im Fluss (display:block im SCSS) statt in der
 * Rahmenkante - der Rahmen ist hier abgeschaltet.
 */
function FormGroup({ step, group, children }) {
	return (
		<fieldset className={styles.group}>
			<legend className={styles.groupHead}>
				<span className={styles.groupStep} aria-hidden="true">
					{step}
				</span>
				<span className={styles.groupTitle}>{group.title}</span>
			</legend>
			{group.hint && <p className={styles.groupHint}>{group.hint}</p>}
			{children}
		</fieldset>
	)
}

/**
 * Öffentliche Seite: erklärt das Unterstützer:innen-Programm und nimmt
 * Selbstregistrierungen entgegen.
 *
 * Bewusst außerhalb von /auth und ohne Login-Kontext: Die Seite wird aus
 * JAA-Mails und von der Junior-Achievement-Website verlinkt, Besucher:innen
 * kennen das System noch nicht. Deshalb steht der Erklärteil vor dem Formular.
 *
 * Die Texte liegen in content.js und sind ein von JAA zu prüfender Entwurf.
 */
export default function SupporterRegister() {
	const { t, i18n } = useTranslation()
	const history = useHistory()
	const content = getRegisterContent(i18n.language)

	const [values, setValues] = useState({
		firstName: '',
		lastName: '',
		email: '',
		password: '',
		repeat: '',
		company: '',
		position: ''
	})
	const [states, setStates] = useState([])
	const [roles, setRoles] = useState([TSupporterRoles.EXPERT])
	// Abschnitt 4 „Junior Company Teilnahme" (Anforderung #181), Vorgabe Nein.
	const [alumni, setAlumni] = useState(emptyJuniorAlumni)
	const [gdpr, setGdpr] = useState(false)
	const [errors, setErrors] = useState({})
	const [isSaving, setSaving] = useState(false)
	const [isDone, setDone] = useState(false)
	const formRef = useRef(null)

	// scrollIntoView gibt es in jsdom nicht und in aelteren Browsern nicht mit
	// Optionsobjekt. Der Sprung ist Komfort, kein Muss - faellt er aus, bleibt
	// die Seite bedienbar.
	const scrollToForm = () => {
		const node = formRef.current
		if (node && typeof node.scrollIntoView === 'function') {
			node.scrollIntoView({ behavior: 'smooth', block: 'start' })
		}
	}

	// Die App stellt die Sprache im Navbar auf Deutsch. Diese Seite laeuft
	// ausserhalb davon (oeffentliche Route ohne Layout), deshalb muss es hier
	// selbst passieren - sonst greift die Browsererkennung und die Seite
	// erscheint auf Englisch.
	//
	// Bewusst ueber die konfigurierte Instanz statt ueber useTranslation():
	// ohne initialisiertes i18next liefert der Hook nur einen Platzhalter ohne
	// changeLanguage.
	useEffect(() => {
		if (i18nInstance.language !== 'ger') i18nInstance.changeLanguage('ger')
	}, [])

	const setValue = (key, value) => setValues(current => ({ ...current, [key]: value }))

	const toggleRole = role =>
		setRoles(current => (current.includes(role) ? current.filter(item => item !== role) : [...current, role]))

	const validate = () => {
		const next = {}

		if (values.firstName.trim().length < 2) next.firstName = t('Required field')
		if (values.lastName.trim().length < 2) next.lastName = t('Required field')
		if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email)) next.email = t('Invalid email')
		if (values.password.length < 8) next.password = t('passwordMinLength')
		if (values.password !== values.repeat) next.repeat = t('passwordsDoNotMatch')
		if (!roles.length) next.roles = t('atLeastOneRole')
		if (!gdpr) next.gdpr = t('Required field')
		// Die Junior-Company-Felder liefern i18n-Schlüssel; übersetzt wird am Feld.
		const alumniErrors = juniorAlumniErrors(alumni)
		if (Object.keys(alumniErrors).length) next.alumni = alumniErrors

		setErrors(next)
		return !Object.keys(next).length
	}

	const submit = event => {
		event.preventDefault()
		if (!validate()) return

		setSaving(true)
		http.post(TApi.PUBLIC_SUPPORTER_REGISTER, {
			firstName: values.firstName,
			lastName: values.lastName,
			email: values.email,
			password: values.password,
			roles,
			company: values.company || null,
			position: values.position || null,
			targetStates: states,
			...juniorAlumniPayload(alumni),
			gdprConsent: true
		})
			.then(() => setDone(true))
			.catch(error => {
				const message = error?.response?.data?.message
				// 409 = Adresse bereits registriert. Das ist der haeufigste Fall und
				// gehoert ans Feld, nicht in einen fluechtigen Toast.
				if (error?.response?.status === 409) {
					setErrors({ email: message || t('emailAlreadyRegistered') })
				} else {
					setErrors({ form: message || t('Something went wrong') })
				}
			})
			.finally(() => setSaving(false))
	}

	if (isDone) {
		return (
			<div className={styles.root}>
				<div className={clsx(styles.card, styles.successCard)}>
					{/* Eigene Logo- und Textklassen: .logo und .lead sind fuer den
					    dunklen Hero eingefaerbt (weiss) und waeren auf der weissen
					    Karte unsichtbar. */}
					<img src={logoIcon} alt="Junior Achievement" className={styles.successLogo} />
					<h1 className={styles.successTitle}>{content.successTitle}</h1>
					<p className={styles.successText}>{content.successText}</p>
					<p className={styles.hint}>{content.successHint}</p>
					<Btn onClick={() => history.push(TRoutes.SIGN_IN)}>{t('Login')}</Btn>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.root}>
			<header className={styles.hero}>
				<div className={styles.heroMedia}>
					<Photo
						sources={HERO_PHOTO}
						alt=""
						loading="eager"
						width={1600}
						height={1067}
						className={styles.heroImg}
					/>
					<span className={styles.heroTint} aria-hidden="true" />
					<span className={styles.heroGrain} aria-hidden="true" />
					<span className={styles.heroScrim} aria-hidden="true" />
				</div>

				<div className={styles.heroInner}>
					<img src={logoIcon} alt="Junior Achievement" className={styles.logo} />
					<span className={styles.eyebrow}>{content.eyebrow}</span>
					<h1 className={styles.headline}>{content.headline}</h1>
					<p className={styles.lead}>{content.intro}</p>

					<div className={styles.heroActions}>
						<button type="button" className={styles.heroCta} onClick={scrollToForm}>
							{content.heroCta}
						</button>
						<div className={styles.heroChips}>
							{content.roles.map((role, index) => (
								<span
									key={role.key}
									className={clsx(styles.heroChip, styles[`chip${role.key}`])}
									style={{ animationDelay: `${index * 0.7}s` }}
								>
									{role.title}
								</span>
							))}
						</div>
					</div>
				</div>

				{/* Kraeuselkante statt harter Schnittlinie zum hellen Inhalt darunter */}
				<svg className={styles.wave} viewBox="0 0 1440 90" preserveAspectRatio="none" aria-hidden="true">
					<path d="M0,52 C220,96 360,8 720,36 C1080,64 1240,4 1440,40 L1440,90 L0,90 Z" />
				</svg>
			</header>

			<Section className={styles.gallerySection}>
				<h2 className={styles.sectionTitle}>{content.galleryTitle}</h2>
				<div className={styles.gallery}>
					{content.gallery.map((item, index) => (
						<figure
							key={item.file}
							className={styles.shot}
							style={{ animationDelay: `${(index % 3) * 1.1}s` }}
						>
							<div className={styles.shotFrame}>
								<Photo
									sources={PHOTOS[item.file]}
									alt={item.caption}
									width={900}
									height={600}
									className={styles.shotImg}
								/>
							</div>
							<figcaption className={styles.shotCaption}>{item.caption}</figcaption>
						</figure>
					))}
				</div>
			</Section>

			{/*
			 * Der Impact-Block: was aus dem Programm schon geworden ist, mit dem
			 * Weg zur öffentlichen Junior-Bilanz. Er steht vor „Drei Arten zu
			 * unterstützen" — wer überlegt mitzumachen, will erst sehen, worauf
			 * er sich einlässt.
			 *
			 * Der Bericht liegt unter einer eigenen, offenen Adresse und wird
			 * deshalb mit `Link` geöffnet, nicht in einem neuen Fenster: Wer
			 * zurückwill, nimmt den Zurück-Knopf, und das Formular auf dieser
			 * Seite bleibt erhalten.
			 */}
			<Section className={styles.impactSection}>
				<h2 className={styles.sectionTitle}>{content.impactTitle}</h2>
				<p className={styles.sectionHint}>{content.impactLead}</p>

				<div className={styles.impactFigures}>
					{content.impactFigures.map(figure => (
						<div key={figure.label} className={styles.impactFigure}>
							<span className={styles.impactValue}>{figure.value}</span>
							<span className={styles.impactLabel}>{figure.label}</span>
						</div>
					))}
				</div>

				<div className={styles.impactActions}>
					<Link to={TRoutes.PUBLIC_ANNUAL_REPORT} className={styles.impactCta}>
						{content.impactCta}
					</Link>
					<span className={styles.impactNote}>{content.impactNote}</span>
				</div>

				<p className={styles.impactJoin}>
					{content.impactJoin}{' '}
					<button type="button" className={styles.impactJoinCta} onClick={scrollToForm}>
						{content.impactJoinCta}
					</button>
				</p>
			</Section>

			<Section className={styles.section}>
				<h2 className={styles.sectionTitle}>{content.rolesTitle}</h2>
				<p className={styles.sectionHint}>{content.rolesHint}</p>
				<div className={styles.roleGrid}>
					{content.roles.map(role => (
						<article key={role.key} className={clsx(styles.roleCard, styles[`role${role.key}`])}>
							<span className={styles.roleAccent} aria-hidden="true" />
							<h3 className={styles.roleTitle}>{role.title}</h3>
							<p className={styles.roleText}>{role.text}</p>
						</article>
					))}
				</div>
			</Section>

			<Section className={styles.section}>
				<h2 className={styles.sectionTitle}>{content.stepsTitle}</h2>
				<ol className={styles.steps}>
					{content.steps.map((step, index) => (
						<li key={index} className={styles.step}>
							<span className={styles.stepNumber}>{index + 1}</span>
							<span className={styles.stepText}>{step}</span>
						</li>
					))}
				</ol>
			</Section>

			<Section className={styles.band}>
				<div className={styles.twoColumn}>
					<div>
						<h2 className={styles.sectionTitle}>{content.commitmentTitle}</h2>
						<p className={styles.bodyText}>{content.commitment}</p>
					</div>
					<div>
						<h2 className={styles.sectionTitle}>{content.privacyTitle}</h2>
						<p className={styles.bodyText}>{content.privacy}</p>
					</div>
				</div>
			</Section>

			<section className={styles.formSection} id="registrierung" ref={formRef}>
				<form className={styles.card} onSubmit={submit}>
					{/* Eigener Kopfbereich in Markenfarbe: Er hebt das Formular vom
					    Rest der Seite ab und macht auf einen Blick klar, dass hier
					    der eigentliche Zweck der Seite liegt. */}
					<div className={styles.formHead}>
						<span className={styles.formEyebrow}>{content.formEyebrow}</span>
						<h2 className={styles.formTitle}>{content.formTitle}</h2>
						<p className={styles.formLead}>{content.formHint}</p>
					</div>

					<div className={styles.formBody}>
						<p className={styles.requiredNote}>{content.formRequired}</p>

						<FormGroup step={1} group={content.formGroups[0]}>
							<div className={styles.grid}>
								<PlainInput
									label={t('firstName')}
									required
									name="firstName"
									autoComplete="given-name"
									value={values.firstName}
									error={errors.firstName}
									onChange={e => setValue('firstName', e.target.value)}
								/>
								<PlainInput
									label={t('lastName')}
									required
									name="lastName"
									autoComplete="family-name"
									value={values.lastName}
									error={errors.lastName}
									onChange={e => setValue('lastName', e.target.value)}
								/>
								<PlainInput
									label={t('company')}
									name="company"
									autoComplete="organization"
									value={values.company}
									onChange={e => setValue('company', e.target.value)}
								/>
								<PlainInput
									label={t('position')}
									name="position"
									autoComplete="organization-title"
									value={values.position}
									onChange={e => setValue('position', e.target.value)}
								/>
							</div>
						</FormGroup>

						<FormGroup step={2} group={content.formGroups[1]}>
							<div className={styles.grid}>
								{/* Die Adresse ist der Anker des Zugangs und steht
								    deshalb allein in der ersten Zeile. */}
								<PlainInput
									className={styles.wide}
									label={t('email')}
									required
									name="email"
									type="email"
									autoComplete="email"
									value={values.email}
									error={errors.email}
									onChange={e => setValue('email', e.target.value)}
								/>
								<PlainInput
									label={t('password')}
									required
									name="password"
									type="password"
									autoComplete="new-password"
									value={values.password}
									error={errors.password}
									onChange={e => setValue('password', e.target.value)}
								/>
								<PlainInput
									label={t('repeatPassword')}
									required
									name="repeatPassword"
									type="password"
									autoComplete="new-password"
									value={values.repeat}
									error={errors.repeat}
									onChange={e => setValue('repeat', e.target.value)}
								/>
							</div>
							<p className={styles.fieldHint}>{content.formPasswordHint}</p>
						</FormGroup>

						<FormGroup step={3} group={content.formGroups[2]}>
							<span className={styles.fieldLabel}>
								{t('possibleRoles')}
								<span className={styles.fieldLabelHint}>{content.formRolesHint}</span>
							</span>
							<div className={styles.roleChoice}>
								{content.roles.map(role => {
									const on = roles.includes(role.key)
									return (
										<button
											key={role.key}
											type="button"
											aria-pressed={on}
											className={clsx(
												styles.roleChip,
												styles[`pick${role.key}`],
												on && styles.roleChipOn
											)}
											onClick={() => toggleRole(role.key)}
										>
											<span className={styles.roleTick} aria-hidden="true" />
											{role.title}
										</button>
									)
								})}
							</div>
							{errors.roles && <span className={styles.error}>{errors.roles}</span>}

							<StatesField value={states} onChange={setStates} />
						</FormGroup>

						<FormGroup step={4} group={content.formGroups[3]}>
							<JuniorAlumniField
								value={alumni}
								errors={errors.alumni}
								onChange={changes => setAlumni(current => ({ ...current, ...changes }))}
							/>
						</FormGroup>

						<div className={styles.formFoot}>
							<label className={styles.checkboxRow}>
								<input type="checkbox" checked={gdpr} onChange={e => setGdpr(e.target.checked)} />
								<span>{t('gdprConsent')}</span>
							</label>
							<GdprConsentNote className={styles.consentNote} />
							{errors.gdpr && <span className={styles.error}>{errors.gdpr}</span>}

							{errors.form && <span className={styles.error}>{errors.form}</span>}

							<Btn type="submit" theme="success" disabled={isSaving} className={styles.submit}>
								{t('supporterRegistration')}
							</Btn>

							<p className={styles.submitHint}>{content.formSubmitHint}</p>

							<p className={styles.loginHint}>
								{t('alreadyRegistered')}{' '}
								<button
									type="button"
									className={styles.linkButton}
									onClick={() => history.push(TRoutes.SIGN_IN)}
								>
									{t('Login')}
								</button>
							</p>
						</div>
					</div>
				</form>
			</section>
		</div>
	)
}
